// Site service-profile (1:1) editor — service intent + support posture.
// 404-aware create/edit like PublicationEditor. No hard-required field (the
// profile only needs to exist for the render); submit blocked only on invalid
// vendor JSON.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { siteServiceProfile } from "../../api/endpoints/siteServiceProfile";
import { qk } from "../../api/queryKeys";
import { ApiError } from "../../api/errors";
import {
  SUPPORT_SCOPES,
  type ServiceProfileConfig,
  type ServiceProfileIn,
  type SupportScope,
} from "../../api/types/siteServiceProfile";
import { FormShell } from "../../components/forms/FormShell";
import { SelectField } from "../../components/forms/SelectField";
import { CheckboxField } from "../../components/forms/CheckboxField";
import { StringListField } from "../../components/forms/StringListField";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { EmptyState } from "../../components/ui/EmptyState";
import { parseVendorExtras } from "../../lib/fieldValidators";

const DEFAULTS: ServiceProfileIn = {
  homeassistant_enabled: false, frigate_enabled: false, mosquitto_enabled: false,
  go2rtc_enabled: false, solar_enabled: false, custom_connectors: [],
  support_scope: null, orixen_support_required: true, special_constraints: [], notes: [], vendor_extras: {},
};

const SCOPE_OPTIONS = [{ value: "", label: "— unset —" }, ...SUPPORT_SCOPES];
const FLAGS: ReadonlyArray<[keyof ServiceProfileIn, string]> = [
  ["homeassistant_enabled", "Home Assistant"],
  ["frigate_enabled", "Frigate"],
  ["mosquitto_enabled", "Mosquitto (MQTT)"],
  ["go2rtc_enabled", "go2rtc"],
  ["solar_enabled", "Solar"],
];

export function ServiceProfileEditor({ siteSlug, canWrite }: { siteSlug: string; canWrite: boolean }) {
  const [creating, setCreating] = useState(false);
  const q = useQuery({
    retry: false,
    queryKey: qk.siteServiceProfile(siteSlug),
    queryFn: () => siteServiceProfile.get(siteSlug).then((r) => r.data),
  });

  if (q.isPending) return <p style={{ color: "var(--text-dim)" }}>Loading service profile…</p>;
  const is404 = q.error instanceof ApiError && q.error.status === 404;
  if (q.error && !is404) return <ErrorBanner error={q.error} title="Failed to load service profile" />;

  if (q.data) return <ProfileForm key={`existing-${siteSlug}`} siteSlug={siteSlug} canWrite={canWrite} initial={toIn(q.data)} existing />;
  if (creating) return <ProfileForm key={`new-${siteSlug}`} siteSlug={siteSlug} canWrite={canWrite} initial={DEFAULTS} onCancel={() => setCreating(false)} />;
  return (
    <EmptyState
      title="No service profile"
      description="Which services this site intends to run + the support posture."
      action={canWrite ? <button type="button" style={primaryBtn} onClick={() => setCreating(true)}>Configure service profile</button> : null}
    />
  );
}

interface ProfileDraft {
  homeassistant_enabled: boolean; frigate_enabled: boolean; mosquitto_enabled: boolean;
  go2rtc_enabled: boolean; solar_enabled: boolean; custom_connectors: string[];
  support_scope: SupportScope | ""; orixen_support_required: boolean;
  special_constraints: string[]; notes: string[]; vendor_extras_json: string;
}

function ProfileForm({
  siteSlug, canWrite, initial, existing, onCancel,
}: {
  siteSlug: string; canWrite: boolean; initial: ServiceProfileIn; existing?: boolean; onCancel?: () => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<ProfileDraft>(() => toDraft(initial));
  const set = <K extends keyof ProfileDraft>(k: K, v: ProfileDraft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const save = useMutation({
    mutationFn: (body: ServiceProfileIn) => siteServiceProfile.put(siteSlug, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: qk.siteServiceProfile(siteSlug) }); onCancel?.(); },
  });
  const del = useMutation({
    mutationFn: () => siteServiceProfile.remove(siteSlug),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.siteServiceProfile(siteSlug) }),
  });

  const vendor = parseVendorExtras(draft.vendor_extras_json);
  const pending = save.isPending || del.isPending;
  const disabled = !canWrite || pending;

  return (
    <FormShell
      submitLabel={existing ? "Save service profile" : "Create service profile"}
      onSubmit={(e) => { e.preventDefault(); if (!vendor.ok || disabled) return; save.mutate(fromDraft(draft, vendor.ok ? vendor.value : {})); }}
      onCancel={onCancel}
      pending={pending}
      submitDisabled={!canWrite || !vendor.ok}
      error={save.error ?? del.error}
      errorTitle="Service profile save failed"
      footer={existing && canWrite ? (
        <div>
          <button type="button" style={dangerBtn} disabled={pending}
            onClick={() => { if (window.confirm(`Delete service profile for ${siteSlug}?`)) del.mutate(); }}>
            Delete service profile
          </button>
        </div>
      ) : undefined}
    >
      {FLAGS.map(([k, label]) => (
        <CheckboxField key={k} label={label} checked={Boolean(draft[k as keyof ProfileDraft])}
          onChange={(v) => set(k as keyof ProfileDraft, v as never)} disabled={disabled} />
      ))}
      <StringListField label="Custom connectors" value={draft.custom_connectors}
        onChange={(v) => set("custom_connectors", v)} placeholder={"tuya\ngoodwe"} helper="One per line." disabled={disabled} />
      <SelectField label="Support scope" value={draft.support_scope}
        onChange={(v) => set("support_scope", v as SupportScope | "")} options={SCOPE_OPTIONS}
        helper="Leave unset if not yet decided." disabled={disabled} />
      <CheckboxField label="Orixen support required" checked={draft.orixen_support_required}
        onChange={(v) => set("orixen_support_required", v)} helper="Managed-by-default." disabled={disabled} />
      <StringListField label="Special constraints" value={draft.special_constraints}
        onChange={(v) => set("special_constraints", v)} helper="One per line." disabled={disabled} />
      <StringListField label="Notes" value={draft.notes} onChange={(v) => set("notes", v)} helper="One per line." disabled={disabled} />
      <details>
        <summary style={{ cursor: "pointer", fontSize: "0.8rem", color: "var(--text-dim)" }}>Vendor extras (advanced, JSON)</summary>
        <textarea value={draft.vendor_extras_json} disabled={disabled} rows={5}
          onChange={(e) => set("vendor_extras_json", e.target.value)} placeholder={"{}"}
          style={{ width: "100%", marginTop: 8, padding: "8px 10px", background: "var(--surface-2)",
            border: `1px solid ${vendor.ok ? "var(--border)" : "var(--crit)"}`, borderRadius: 4,
            color: "var(--text)", fontFamily: "monospace", fontSize: "0.85rem", resize: "vertical" }} />
        {!vendor.ok && <span style={{ fontSize: "0.75rem", color: "var(--crit)" }}>{vendor.error}</span>}
      </details>
    </FormShell>
  );
}

function toIn(p: ServiceProfileConfig): ServiceProfileIn {
  const { site_id: _site_id, ...rest } = p;
  return rest;
}
function toDraft(p: ServiceProfileIn): ProfileDraft {
  const hasExtras = Object.keys(p.vendor_extras ?? {}).length > 0;
  return {
    homeassistant_enabled: Boolean(p.homeassistant_enabled), frigate_enabled: Boolean(p.frigate_enabled),
    mosquitto_enabled: Boolean(p.mosquitto_enabled), go2rtc_enabled: Boolean(p.go2rtc_enabled),
    solar_enabled: Boolean(p.solar_enabled), custom_connectors: p.custom_connectors ?? [],
    support_scope: p.support_scope ?? "", orixen_support_required: p.orixen_support_required ?? true,
    special_constraints: p.special_constraints ?? [], notes: p.notes ?? [],
    vendor_extras_json: hasExtras ? JSON.stringify(p.vendor_extras, null, 2) : "",
  };
}
function fromDraft(d: ProfileDraft, vendorExtras: Record<string, unknown>): ServiceProfileIn {
  return {
    homeassistant_enabled: d.homeassistant_enabled, frigate_enabled: d.frigate_enabled,
    mosquitto_enabled: d.mosquitto_enabled, go2rtc_enabled: d.go2rtc_enabled, solar_enabled: d.solar_enabled,
    custom_connectors: d.custom_connectors, support_scope: d.support_scope === "" ? null : d.support_scope,
    orixen_support_required: d.orixen_support_required, special_constraints: d.special_constraints,
    notes: d.notes, vendor_extras: vendorExtras,
  };
}

const primaryBtn: React.CSSProperties = {
  background: "var(--accent)", color: "var(--bg)", border: "none", padding: "8px 16px",
  borderRadius: 4, fontSize: "0.9rem", fontWeight: 600, cursor: "pointer",
};
const dangerBtn: React.CSSProperties = {
  background: "transparent", color: "var(--crit)", border: "1px solid var(--crit)",
  padding: "6px 12px", borderRadius: 4, fontSize: "0.85rem", cursor: "pointer",
};
